import { Globe, ImagePlus, X } from "lucide-react";
import {
  SEO_ROBOTS_OPTIONS,
  withTrailingSlash,
  buildCanonicalUrl,
} from "./blogSeoSync";

const cardIcon =
  "inline-flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-[#982598]/10 text-[#982598]";

const normalizeImageUrl = (url) => {
  if (!url || typeof url !== "string") return url;
  if (url.startsWith("blob:") || url.startsWith("data:")) return url;
  let normalized = url.replace(/\/api(?=\/)/, "");
  if (
    typeof window !== "undefined" &&
    window.location?.protocol === "https:" &&
    normalized.startsWith("http://")
  ) {
    normalized = "https:" + normalized.slice(5);
  }
  return normalized;
};

/**
 * Blog page SEO fields (Approach A — dirty-flag sync handled by parent).
 */
export function BlogSeoSection({
  seoForm,
  seoDirty,
  seoFieldErrors = {},
  ogImagePreview,
  onFieldChange,
  onSeoFormChange,
  onOgImageChange,
  onRemoveOgImage,
}) {
  const handleSlugBlur = () => {
    onSeoFormChange((prev) => {
      if (!prev.slug.trim()) return prev;
      const normalized = withTrailingSlash(prev.slug);
      const next = {
        ...prev,
        slug: normalized,
      };
      if (!seoDirty.canonical_url) {
        next.canonical_url = buildCanonicalUrl(normalized);
      }
      if (
        next.slug === prev.slug &&
        next.canonical_url === prev.canonical_url
      ) {
        return prev;
      }
      return next;
    });
  };

  return (
    <section className="ui-card">
      <div className="mb-4 flex items-center gap-2.5">
        <span className={cardIcon}>
          <Globe size={15} />
        </span>
        <div>
          <h3 className="text-[14px] font-extrabold text-[#15173d]">SEO</h3>
          <p className="text-[11px] text-gray-500">
            Blog Name is master — changing it updates meta/OG/image titles
            everywhere.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div className="sm:col-span-2">
          <label className="ui-form-label">
            Meta Title <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={seoForm.meta_title}
            onChange={(e) => onFieldChange("meta_title", e.target.value)}
            placeholder="Page title for search engines"
            className="ui-input-field"
          />
          {seoFieldErrors.meta_title && (
            <p className="mt-1 text-[11px] text-red-500">
              {seoFieldErrors.meta_title}
            </p>
          )}
        </div>

        <div className="sm:col-span-2">
          <label className="ui-form-label">
            Meta Description <span className="text-red-500">*</span>
          </label>
          <textarea
            value={seoForm.meta_description}
            onChange={(e) => onFieldChange("meta_description", e.target.value)}
            placeholder="Short description for search results"
            className="ui-textarea-field min-h-[72px]"
          />
          {seoFieldErrors.meta_description && (
            <p className="mt-1 text-[11px] text-red-500">
              {seoFieldErrors.meta_description}
            </p>
          )}
        </div>

        <div className="sm:col-span-2">
          <label className="ui-form-label">
            Meta Keywords <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={seoForm.meta_keywords}
            onChange={(e) => onFieldChange("meta_keywords", e.target.value)}
            placeholder="keyword1, keyword2, keyword3"
            className="ui-input-field"
          />
          {seoFieldErrors.meta_keywords && (
            <p className="mt-1 text-[11px] text-red-500">
              {seoFieldErrors.meta_keywords}
            </p>
          )}
        </div>

        <div>
          <label className="ui-form-label">
            Slug <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={seoForm.slug}
            onChange={(e) => onFieldChange("slug", e.target.value)}
            onBlur={handleSlugBlur}
            placeholder="url-friendly-slug/"
            className="ui-input-field"
          />
          {seoFieldErrors.slug && (
            <p className="mt-1 text-[11px] text-red-500">
              {seoFieldErrors.slug}
            </p>
          )}
        </div>

        <div>
          <label className="ui-form-label">
            Canonical URL <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={seoForm.canonical_url}
            onChange={(e) => onFieldChange("canonical_url", e.target.value)}
            placeholder="https://gharplans.pk/your-slug/"
            className="ui-input-field"
          />
          {seoFieldErrors.canonical_url && (
            <p className="mt-1 text-[11px] text-red-500">
              {seoFieldErrors.canonical_url}
            </p>
          )}
          {!seoFieldErrors.canonical_url && (
            <p className="mt-1 text-[11px] text-gray-400">
              Auto: website base + slug (e.g. https://gharplans.pk/your-slug/)
            </p>
          )}
        </div>

        <div>
          <label className="ui-form-label">
            Robots <span className="text-red-500">*</span>
          </label>
          <select
            value={seoForm.robots}
            onChange={(e) => onFieldChange("robots", e.target.value)}
            className="ui-select-field"
          >
            {SEO_ROBOTS_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="ui-form-label">
            OG Type <span className="text-red-500">*</span>
          </label>
          <select
            value={seoForm.og_type}
            onChange={(e) => onFieldChange("og_type", e.target.value)}
            className="ui-select-field"
          >
            <option value="article">Article</option>
            <option value="website">Website</option>
            <option value="product">Product</option>
          </select>
        </div>

        <div className="sm:col-span-2">
          <label className="ui-form-label">OG Title</label>
          <input
            type="text"
            value={seoForm.og_title}
            onChange={(e) => onFieldChange("og_title", e.target.value)}
            placeholder="Uses meta title if left empty"
            className="ui-input-field"
          />
        </div>

        <div className="sm:col-span-2">
          <label className="ui-form-label">OG Description</label>
          <textarea
            value={seoForm.og_description}
            onChange={(e) => onFieldChange("og_description", e.target.value)}
            placeholder="Uses meta description if left empty"
            className="ui-textarea-field min-h-[64px]"
          />
        </div>

        <div className="sm:col-span-2">
          <label className="ui-form-label">
            OG Image <span className="text-red-500">*</span>
          </label>
          {ogImagePreview ? (
            <div className="relative overflow-hidden rounded-xl border border-gray-200">
              <img
                src={normalizeImageUrl(ogImagePreview)}
                alt="OG preview"
                className="h-40 w-full object-cover"
              />
              <button
                type="button"
                onClick={onRemoveOgImage}
                className="absolute right-2 top-2 rounded-full bg-red-500 p-1.5 text-white shadow"
                title="Remove OG image"
              >
                <X size={14} />
              </button>
            </div>
          ) : (
            <label className="flex h-28 cursor-pointer flex-col items-center justify-center gap-1 rounded-xl border border-dashed border-gray-300 bg-[#faf8fa] text-[12px] text-gray-500 hover:border-[#982598]">
              <ImagePlus size={20} className="text-[#982598]" />
              Upload OG image (or blog cover will be used)
              <span className="text-[11px] text-gray-400">
                Required size: 1200 × 630 px
              </span>
              <input
                type="file"
                accept="image/*"
                onChange={onOgImageChange}
                className="hidden"
              />
            </label>
          )}
          {seoFieldErrors.og_image && (
            <p className="mt-1 text-[11px] text-red-500">
              {seoFieldErrors.og_image}
            </p>
          )}
          {!seoFieldErrors.og_image && (
            <p className="mt-1 text-[11px] text-gray-400">
              OG image must be exactly 1200 × 630 pixels.
            </p>
          )}
        </div>

        <div className="sm:col-span-2">
          <label className="ui-form-label">OG Image Alt</label>
          <input
            type="text"
            value={seoForm.og_image_alt}
            onChange={(e) => onFieldChange("og_image_alt", e.target.value)}
            placeholder="Alt text for OG image"
            className="ui-input-field"
          />
        </div>
      </div>
    </section>
  );
}
